import React, { useEffect, useState } from "react";
import {
  addLabIncharge,
  getAllLabIncharges,
  updateLabIncharge,
  deleteLabIncharge,
  updateLabInchargePassword,
} from "actions/action";
import { getAllLabs } from "actions/action";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";
import { motion } from "framer-motion";
import Swal from "sweetalert2";
import ReactDOMServer from "react-dom/server";

export default function LabIncharge() {
  const [labIncharges, setLabIncharges] = useState([]);
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    password: "",
    empid: "",
    lab_id: "",
  });
  const [createForm, setCreateForm] = useState({
    name: "",
    email: "",
    password: "",
    empid: "",
    lab_id: "",
  });

  const fetchLabIncharges = async () => {
    setLoading(true);
    try {
      const res = await getAllLabIncharges();
      if (res?.err) throw new Error();
      const data = Array.isArray(res?.data) ? res.data : [];
      setLabIncharges(data);
      setError("");
    } catch (e) {
      setError("Failed to load lab incharges");
    } finally {
      setLoading(false);
    }
  };

  const fetchLabs = async () => {
    try {
      const res = await getAllLabs();
      if (res?.err) throw new Error();
      const data = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.labs)
        ? res.labs
        : Array.isArray(res?.data?.data)
        ? res.data.data
        : [];
      setLabs(data);
    } catch (e) {
      console.log("Failed to load labs:", e);
    }
  };

  useEffect(() => {
    fetchLabIncharges();
    fetchLabs();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (
      !createForm.name.trim() ||
      !createForm.email.trim() ||
      !createForm.password.trim()
    )
      return;
    setCreating(true);
    try {
      const res = await addLabIncharge(createForm);
      if (res?.err) throw new Error();
      setCreateForm({
        name: "",
        email: "",
        password: "",
        empid: "",
        lab_id: "",
      });
      await fetchLabIncharges();
    } catch (e) {
      setError("Failed to create lab incharge");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (incharge) => {
    setEditingId(incharge.lab_incharge_id);
    setEditForm({
      name: incharge.name || "",
      email: incharge.email || "",
      empid: incharge.empid || "",
      lab_id: incharge.lab_id || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: "", email: "", empid: "", lab_id: "" });
  };

  const saveEdit = async (id) => {
    if (!editForm.name.trim() || !editForm.email.trim()) return;
    try {
      const res = await updateLabIncharge(id, editForm);
      if (res?.err) throw new Error();
      cancelEdit();
      await fetchLabIncharges();
    } catch {
      setError("Failed to update lab incharge");
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete these records?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: ReactDOMServer.renderToString(
        <>
          Yes, delete it!
          <FontAwesomeIcon icon={faTrash} style={{ marginLeft: "10px" }} />
        </>
      ),
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteLabIncharge(id).then((res) => {
          if (res?.err) throw new Error();
          fetchLabIncharges();
          Swal.fire({
            title: "Deleted!",
            text: "Lab incharge record deleted successfully.",
            icon: "success",
            timer: 2000,
            showConfirmButton: false,
          });
        }).catch(() => {
          setError("Failed to delete lab incharge");
        });
      }
    });
  };

  const handlePasswordUpdate = async (id, newPassword) => {
    try {
      const res = await updateLabInchargePassword(id, newPassword);
      if (res?.err) throw new Error();
      alert("Password updated successfully");
    } catch {
      setError("Failed to update password");
    }
  };

  return (
    <>
      <div className="relative">
        <div className="flex justify-between items-center px-4 mt-6">
          <p className="font-semibold text-lg text-white">Lab Incharge Details</p>
        </div>

        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-blueGray-700">
          <div className="block w-full overflow-x-auto p-4">
              {/* Create Form */}
              <form
                className="mb-6 p-4 bg-blueGray-800 rounded"
                onSubmit={handleCreate}
              >
                <h4 className="text-lg font-semibold mb-4 text-white">
                  Add New Lab Incharge
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-white text-xs font-bold mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-800 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                      placeholder="Enter name"
                      value={createForm.name}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, name: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xs font-bold mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-800 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                      placeholder="Enter email"
                      value={createForm.email}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, email: e.target.value })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xs font-bold mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-800 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                      placeholder="Enter password"
                      value={createForm.password}
                      onChange={(e) =>
                        setCreateForm({
                          ...createForm,
                          password: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xs font-bold mb-2">
                      Employee ID
                    </label>
                    <input
                      type="text"
                      className="border-0 px-3 py-3 placeholder-blueGray-400 text-white bg-blueGray-800 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                      placeholder="Enter employee ID"
                      value={createForm.empid}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, empid: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-white text-xs font-bold mb-2">
                      Lab *
                    </label>
                    <select
                      className="border-0 px-3 py-3 text-white bg-blueGray-800 rounded text-sm shadow outline-none focus:outline-none focus:ring w-full"
                      value={createForm.lab_id}
                      onChange={(e) =>
                        setCreateForm({ ...createForm, lab_id: e.target.value })
                      }
                      required
                    >
                      <option value="">Select Lab</option>
                      {labs.map((lab) => (
                        <option
                          key={lab.lab_id || lab.id}
                          value={lab.lab_id || lab.id}
                        >
                          {lab.lab_name || lab.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button
                      type="submit"
                      disabled={creating}
                      className="bg-emerald-500 text-white active:bg-emerald-600 text-xs font-bold uppercase px-4 py-3 rounded shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150 disabled:opacity-60"
                    >
                      {creating ? "Creating..." : "Create Lab Incharge"}
                    </button>
                  </div>
                </div>
              </form>

              {error && (
                <div className="text-red-500 text-sm mb-4">{error}</div>
              )}

              {loading ? (
                <div className="text-white">Loading...</div>
              ) : labIncharges.length === 0 ? (
                <div className="text-white">No lab incharges found.</div>
              ) : (
                <table className="items-center w-full bg-transparent border-collapse">
                  <thead>
                    <tr>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        S.No.
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Name
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Email
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Employee ID
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Lab
                      </th>
                      <th className="px-6 align-middle border border-solid py-3 text-xs uppercase border-l-0 border-r-0 whitespace-nowrap font-semibold text-left bg-blueGray-700 text-white border-blueGray-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {labIncharges.map((incharge, index) => (
                      <tr key={incharge.lab_incharge_id}>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {index + 1}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {editingId === incharge.lab_incharge_id ? (
                            <input
                              className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                              value={editForm.name}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  name: e.target.value,
                                })
                              }
                            />
                          ) : (
                            incharge.name
                          )}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {editingId === incharge.lab_incharge_id ? (
                            <input
                              className="border px-2 py-1 rounded text-sm w-40 bg-blueGray-800 text-white"
                              value={editForm.email}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  email: e.target.value,
                                })
                              }
                            />
                          ) : (
                            incharge.email
                          )}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {editingId === incharge.lab_incharge_id ? (
                            <input
                              className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                              value={editForm.empid}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  empid: e.target.value,
                                })
                              }
                            />
                          ) : (
                            incharge.empid || "-"
                          )}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 border-blueGray-700 text-white">
                          {editingId === incharge.lab_incharge_id ? (
                            <select
                              className="border px-2 py-1 rounded text-sm w-32 bg-blueGray-800 text-white"
                              value={editForm.lab_id}
                              onChange={(e) =>
                                setEditForm({
                                  ...editForm,
                                  lab_id: e.target.value,
                                })
                              }
                            >
                              <option value="">Select Lab</option>
                              {labs.map((lab) => (
                                <option
                                  key={lab.lab_id || lab.id}
                                  value={lab.lab_id || lab.id}
                                >
                                  {lab.lab_name || lab.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            incharge.lab?.lab_name || "-"
                          )}
                        </td>
                        <td className="border-t-0 px-6 align-middle text-sm whitespace-nowrap p-4 text-center flex gap-4">
                          {editingId === incharge.lab_incharge_id ? (
                            <>
                              <button
                                onClick={() =>
                                  saveEdit(incharge.lab_incharge_id)
                                }
                                className="bg-emerald-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                className="bg-blueGray-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit" arrow>
                                <motion.button
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() => startEdit(incharge)}
                                  className="focus:outline-none"
                                  style={{ color: 'var(--accent-primary)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={faPenToSquare}
                                    className="text-xl"
                                  />
                                </motion.button>
                              </Tooltip>
                              <button
                                onClick={() => {
                                  const newPassword = prompt(
                                    "Enter new password:"
                                  );
                                  if (newPassword) {
                                    handlePasswordUpdate(
                                      incharge.lab_incharge_id,
                                      newPassword
                                    );
                                  }
                                }}
                                className="bg-yellow-500 text-white text-xs font-bold uppercase px-3 py-2 rounded shadow hover:shadow-md mr-2"
                              >
                                Reset Password
                              </button>
                              <Tooltip title="Delete" arrow>
                                <motion.button
                                  whileHover={{ scale: 1.2 }}
                                  whileTap={{ scale: 0.9 }}
                                  type="button"
                                  onClick={() =>
                                    handleDelete(incharge.lab_incharge_id)
                                  }
                                  className="focus:outline-none mx-4"
                                  style={{ color: 'var(--danger)' }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    className="text-xl"
                                  />
                                </motion.button>
                              </Tooltip>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
    </>
  );
}
